import { useAuth } from '@/contexts/auth-context';
import { useInvitationStoreSelector } from '@/contexts/invitation-context';
import { useBulkWishlistInvite } from '@/hooks/actions';
import api from '@/libs/axiosInstance';
import { bulkInvitePusherService } from '@/services/bulk-invite-pusher-service';
import useBulkInviteProgressStore, {
  type BulkInviteDelivery,
} from '@/stores/bulk-invite-progress-store';
import { BulkInvitationBatchStatus, type BulkInviteWishlistRequest } from '@fluenceur/types';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

/**
 * Drives a bulk invitation send end to end, shared by the email and link
 * delivery views so the send + progress wiring can never drift between them:
 *
 *  1. subscribes to the brand's Pusher channel (callbacks filter by the real
 *     batch id, so two concurrent batches never pollute each other);
 *  2. POSTs the request and reads the real `batchId` / `totalCount` /
 *     `skippedItems`;
 *  3. starts the progress batch (owned by the modal) and seeds the epilogue
 *     recap context, then routes to the `bulkProgress` view.
 *
 * The subscription is a module singleton that outlives the modal, so a user who
 * closes mid-flight still gets the aggregate toast (ownership transfers there).
 */
export const useBulkInviteSubmit = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { mutateAsync: bulkInvite, isPending } = useBulkWishlistInvite();

  const setCurrentView = useInvitationStoreSelector((state) => state.setCurrentView);
  const setBulkProgress = useInvitationStoreSelector((state) => state.setBulkProgress);

  const startBatch = useBulkInviteProgressStore((state) => state.startBatch);
  const updateProgress = useBulkInviteProgressStore((state) => state.updateProgress);
  const completeBatch = useBulkInviteProgressStore((state) => state.completeBatch);
  const recordDelivery = useBulkInviteProgressStore((state) => state.recordDelivery);
  const clearBatch = useBulkInviteProgressStore((state) => state.clearBatch);

  const submit = useCallback(
    async (payload: BulkInviteWishlistRequest): Promise<boolean> => {
      const brandId = user?.currentBrand?.id;
      if (!brandId) return false;

      // Only touch the store for the batch we started: comparing against the
      // live `activeBatch.batchId` drops a stray event from another batch.
      const isCurrentBatch = (batchId: string) =>
        useBulkInviteProgressStore.getState().activeBatch?.batchId === batchId;

      // A send job can finish before the POST response initializes the batch.
      // startBatch clears outcomes, so replay these early events after it runs.
      const earlyDeliveries = new Map<string, BulkInviteDelivery>();
      let batchStarted = false;
      const recordOutcome = (invitationId: string, delivery: BulkInviteDelivery) => {
        if (batchStarted) recordDelivery(invitationId, delivery);
        else earlyDeliveries.set(invitationId, delivery);
      };

      try {
        // Subscribe BEFORE the POST so no early progress event is missed; the
        // filter ignores anything until our batch id lands post-POST.
        await bulkInvitePusherService.subscribe(brandId, {
          onProgress: (data) => {
            if (!isCurrentBatch(data.batchId)) return;
            updateProgress(data.sent, data.failed);
          },
          onComplete: (data) => {
            if (!isCurrentBatch(data.batchId)) return;
            completeBatch(data.sent, data.failed);
            // Keep the subscription alive: the per-recipient email send jobs run
            // AFTER the batch is queued, so their outreach events still need to
            // land. The recap view tears the subscription down when it closes.
            queryClient.invalidateQueries({ queryKey: ['wishlist'] });
          },
          // Live per-recipient email outcomes, matched to recap lines by
          // invitation id. Not batch-scoped in the event, but the invitation ids
          // are unique to this batch's items.
          onOutreachSent: (data) => recordOutcome(data.invitationId, { status: 'sent' }),
          onOutreachUnknown: (data) => recordOutcome(data.invitationId, { status: 'unknown' }),
          onOutreachFailed: (data) =>
            recordOutcome(data.invitationId, {
              status: 'failed',
              message: data.errorMessage ?? null,
              errorCode: data.errorCode ?? null,
            }),
        });

        const response = await bulkInvite(payload);

        startBatch(response.batchId, response.totalCount, { owner: 'modal' });
        batchStarted = true;
        for (const [invitationId, delivery] of earlyDeliveries)
          recordDelivery(invitationId, delivery);
        earlyDeliveries.clear();
        setBulkProgress({
          batchId: response.batchId,
          deliveryMode: payload.deliveryMode,
          skippedItems: response.skippedItems ?? [],
        });
        setCurrentView('bulkProgress');

        // A very fast batch can complete before `startBatch` armed the event
        // filter (its complete event is then dropped by isCurrentBatch). One
        // status read after arming closes that race deterministically.
        void api
          .get(`/dashboard/wishlist/bulk-invite/${response.batchId}/status`)
          .then(({ data: envelope }) => {
            const batch = envelope?.data;
            if (!batch || !isCurrentBatch(batch.id)) return;
            if (
              batch.status === BulkInvitationBatchStatus.COMPLETED ||
              batch.status === BulkInvitationBatchStatus.FAILED
            ) {
              completeBatch(batch.sentCount ?? 0, batch.failedCount ?? 0);
              // Keep the subscription for the trailing outreach events (see
              // onComplete); the recap view tears it down on close.
              queryClient.invalidateQueries({ queryKey: ['wishlist'] });
            }
          })
          .catch(() => {});

        return true;
      } catch {
        // The mutation hook surfaces the error toast; here we just tear down.
        bulkInvitePusherService.unsubscribe();
        clearBatch();
        return false;
      }
    },
    [
      user?.currentBrand?.id,
      bulkInvite,
      queryClient,
      startBatch,
      updateProgress,
      completeBatch,
      recordDelivery,
      clearBatch,
      setBulkProgress,
      setCurrentView,
    ]
  );

  return { submit, isSubmitting: isPending };
};
